import { useState, useEffect, useCallback } from "react";
import axios from "axios";
import { API } from "@/App";
import { toast } from "sonner";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter,
} from "@/components/ui/dialog";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { useTranslation } from "@/i18n/translations";
import { MapPin, Users, Scissors, Mail, Trash2, Pencil, Plus, Clock, Star, Copy } from "lucide-react";
import { LevelsEditor, Stars } from "@/components/StaffLevels";
import StaffExceptions from "@/components/StaffExceptions";
import StaffPhotoUpload from "@/components/StaffPhotoUpload";

const DAYS = ["mon", "tue", "wed", "thu", "fri", "sat", "sun"];
const DAY_RU = { mon: "Пн", tue: "Вт", wed: "Ср", thu: "Чт", fri: "Пт", sat: "Сб", sun: "Вс" };
const STAFF_COLORS = ["#0ea5e9", "#f59e0b", "#10b981", "#8b5cf6", "#ef4444", "#ec4899", "#14b8a6", "#f97316"];

const emptyBranch = { name: "", address: "", phone: "", work_hours: "", schedule: null };

const defaultBranchSchedule = () => Object.fromEntries(
  DAYS.map((d) => [d, { enabled: !["sat", "sun"].includes(d) ? true : d === "sat", start: "10:00", end: "19:00" }])
);

const scheduleToText = (schedule) => DAYS
  .filter((d) => schedule?.[d]?.enabled)
  .map((d) => `${DAY_RU[d]} ${schedule[d].start}–${schedule[d].end}`)
  .join(" · ");
const emptyStaff = { name: "", email: "", role: "master", branch_ids: [], services: [], color: STAFF_COLORS[0], planity_ical_url: "" };
const emptyService = { name: "", category: "", price: "", duration_min: 60, branch_ids: [], branch_prices: {} };

const groupByCategory = (services, noCatLabel) => {
  const map = new Map();
  services.forEach((sv) => {
    const k = (sv.category || "").trim();
    if (!map.has(k)) map.set(k, []);
    map.get(k).push(sv);
  });
  return [...map.entries()].sort(([a], [b]) => (a === "" ? 1 : b === "" ? -1 : a.localeCompare(b)))
    .map(([k, list]) => ({ key: k, label: k || noCatLabel, list }));
};

export default function TeamManagement() {
  const { t } = useTranslation();
  const [branches, setBranches] = useState([]);
  const [svcBranchTab, setSvcBranchTab] = useState("all");
  const [staffBranchTab, setStaffBranchTab] = useState("all");
  const [openCats, setOpenCats] = useState({});
  const toggleCat = (k) => setOpenCats((o) => ({ ...o, [k]: !o[k] }));
  const moveService = async (list, idx, dir) => {
    const j = idx + dir;
    if (j < 0 || j >= list.length) return;
    const ordered = [...list]; [ordered[idx], ordered[j]] = [ordered[j], ordered[idx]];
    const ids = services.map((sv) => sv.id);
    const groupIds = list.map((sv) => sv.id);
    let k = 0;
    const fullOrder = ids.map((id) => (groupIds.includes(id) ? ordered[k++].id : id));
    try { await axios.put(`${API}/team/services/order`, { ids: fullOrder }); load(); } catch { toast.error(t("team.saveError")); }
  };
  const moveCategory = async (groups, idx, dir) => {
    const named = groups.map((g) => g.key).filter(Boolean);
    const i = named.indexOf(groups[idx].key); const j = i + dir;
    if (i < 0 || j < 0 || j >= named.length) return;
    [named[i], named[j]] = [named[j], named[i]];
    try { await axios.put(`${API}/team/services/category-order`, { categories: named }); load(); } catch { toast.error(t("team.saveError")); }
  };
  const staffInBranch = (st, bid) => !bid || bid === "all" || !(st.branch_ids || []).length || st.branch_ids.includes(bid);
  const svcInBranch = (sv, bid) => !bid || bid === "all" || !(sv.branch_ids || []).length || sv.branch_ids.includes(bid);
  const svcForStaff = (sv, st) => { const sb = st?.branch_ids || []; return !sb.length || !(sv.branch_ids || []).length || sv.branch_ids.some((b) => sb.includes(b)); };
  const [staff, setStaff] = useState([]);
  const [services, setServices] = useState([]);
  const [levels, setLevels] = useState([]);
  const [levelsOpen, setLevelsOpen] = useState(false);
  const [branchDialog, setBranchDialog] = useState(null); // {..} editing or emptyBranch
  const [staffDialog, setStaffDialog] = useState(null);
  const [serviceDialog, setServiceDialog] = useState(null);
  const [scheduleDialog, setScheduleDialog] = useState(null); // staff object
  const [busy, setBusy] = useState(false);
  const [myRole, setMyRole] = useState("owner");
  const [planityCheck, setPlanityCheck] = useState(null); // {loading, result, error}

  const checkPlanity = async () => {
    const url = (staffDialog?.planity_ical_url || "").trim();
    if (!url) return;
    setPlanityCheck({ loading: true });
    try {
      const r = await axios.post(`${API}/team/staff/planity-preview`, { url });
      setPlanityCheck({ result: r.data });
    } catch (e) {
      setPlanityCheck({ error: e?.response?.data?.detail || t("team.planityError") });
    }
  };

  const load = useCallback(async () => {
    try {
      const [b, s, sv, me, lv] = await Promise.all([
        axios.get(`${API}/team/branches`),
        axios.get(`${API}/team/staff`),
        axios.get(`${API}/team/services`),
        axios.get(`${API}/team/me`),
        axios.get(`${API}/team/levels`),
      ]);
      setLevels(lv.data.levels || []);
      setBranches(b.data.branches || []);
      setStaff(s.data.staff || []);
      setServices(sv.data.services || []);
      setMyRole(me.data.role || "owner");
    } catch (e) {
      if (e?.response?.status !== 429) toast.error("Не удалось загрузить данные команды");
    }
  }, []);

  useEffect(() => { load(); }, [load]);

  const saveBranch = async () => {
    setBusy(true);
    try {
      const body = { ...branchDialog };
      if (body.schedule) body.work_hours = scheduleToText(body.schedule);
      if (branchDialog.id) await axios.put(`${API}/team/branches/${branchDialog.id}`, body);
      else await axios.post(`${API}/team/branches`, body);
      toast.success(t("team.saved"));
      setBranchDialog(null);
      load();
    } catch (e) { toast.error(e?.response?.data?.detail || t("team.saveError")); }
    finally { setBusy(false); }
  };

  const deleteBranch = async (id) => {
    try { await axios.delete(`${API}/team/branches/${id}`); toast.success(t("team.deleted")); load(); }
    catch (e) { toast.error(e?.response?.data?.detail || t("team.saveError")); }
  };

  const cleanOverrides = (d) => Object.fromEntries(Object.entries(d.service_overrides || {})
    .filter(([k, v]) => (d.services || []).includes(k) && v && (v.duration_min || v.price != null))
    .map(([k, v]) => [k, { duration_min: v.duration_min || null, price: v.price ?? null }]));
  const saveStaff = async () => {
    setBusy(true);
    try {
      const body = { ...staffDialog, service_overrides: cleanOverrides(staffDialog), email: staffDialog.email || null };
      if (staffDialog.id) await axios.put(`${API}/team/staff/${staffDialog.id}`, body);
      else await axios.post(`${API}/team/staff`, body);
      toast.success(t("team.saved"));
      setStaffDialog(null);
      load();
    } catch (e) { toast.error(e?.response?.data?.detail || t("team.saveError")); }
    finally { setBusy(false); }
  };

  const deleteStaff = async (id) => {
    try { await axios.delete(`${API}/team/staff/${id}`); toast.success(t("team.deleted")); load(); }
    catch (e) { toast.error(e?.response?.data?.detail || t("team.saveError")); }
  };

  const [inviteBusy, setInviteBusy] = useState(false);
  const inviteStaff = async (id) => {
    setInviteBusy(true);
    try {
      const r = await axios.post(`${API}/team/staff/${id}/invite`);
      toast.success(r.data?.recovery ? t("team.accessSent") : t("team.inviteSent"));
      load();
    } catch (e) { toast.error(e?.response?.data?.detail || t("team.inviteError")); } finally { setInviteBusy(false); }
  };

  const saveSchedule = async () => {
    setBusy(true);
    try {
      const rep = scheduleDialog.schedule_repeat || {};
      const every = parseInt(rep.every_weeks) || 1;
      await axios.put(`${API}/team/staff/${scheduleDialog.id}`, {
        name: scheduleDialog.name, role: scheduleDialog.role, schedule: scheduleDialog.schedule,
        schedule_repeat: every > 1 ? { every_weeks: every, anchor: rep.anchor || new Date().toISOString().slice(0, 10), until: rep.until || null } : { every_weeks: 1 },
      });
      toast.success(t("team.saved"));
      setScheduleDialog(null);
      load();
    } catch (e) { toast.error(e?.response?.data?.detail || t("team.saveError")); }
    finally { setBusy(false); }
  };

  const saveService = async () => {
    setBusy(true);
    try {
      const level_prices = Object.fromEntries(Object.entries(serviceDialog.level_prices || {}).filter(([, v]) => v !== "" && v != null && !isNaN(parseFloat(v))).map(([k, v]) => [k, parseFloat(v)]));
      const branch_prices = Object.fromEntries(Object.entries(serviceDialog.branch_prices || {}).filter(([, v]) => v !== "" && v != null && !isNaN(parseFloat(v))).map(([k, v]) => [k, parseFloat(v)]));
      const body = { ...serviceDialog, category: (serviceDialog.category || "").trim() || null, level_prices, branch_prices, branch_ids: serviceDialog.branch_ids || [], price: serviceDialog.price === "" ? null : parseFloat(serviceDialog.price), duration_min: parseInt(serviceDialog.duration_min) || 60 };
      if (serviceDialog.id) await axios.put(`${API}/team/services/${serviceDialog.id}`, body);
      else await axios.post(`${API}/team/services`, body);
      toast.success(t("team.saved"));
      setServiceDialog(null);
      load();
    } catch (e) { toast.error(e?.response?.data?.detail || t("team.saveError")); }
    finally { setBusy(false); }
  };

  const deleteService = async (id) => {
    try { await axios.delete(`${API}/team/services/${id}`); toast.success(t("team.deleted")); load(); }
    catch (e) { toast.error(e?.response?.data?.detail || t("team.saveError")); }
  };

  const branchName = (id) => branches.find((b) => b.id === id)?.name || "";
  const strictMode = staff.some((s) => (s.services || []).length > 0);

  return (
    <div className="space-y-8 text-black" data-testid="team-management">
      {/* ── Branches ── */}
      <section className="bg-white border-2 border-black rounded-lg p-4 sm:p-6">
        <div className="flex items-center justify-between flex-wrap gap-3 mb-4">
          <h3 className="text-lg font-bold flex items-center gap-2"><MapPin className="w-5 h-5" /> {t("team.branchesTitle")}</h3>
          <Button size="sm" data-testid="add-branch-btn" onClick={() => setBranchDialog({ ...emptyBranch, schedule: defaultBranchSchedule() })}
            className="bg-black text-white hover:bg-gray-800 font-semibold"><Plus className="w-4 h-4 mr-1" />{t("team.addBranch")}</Button>
        </div>
        <p className="text-xs text-gray-500 mb-3">{t("team.branchesHint")}</p>
        <div className="grid sm:grid-cols-2 gap-3" data-testid="branches-list">
          {branches.map((b) => (
            <div key={b.id} className="border-2 border-gray-200 rounded-md p-3 flex items-start justify-between gap-2">
              <div className="min-w-0">
                <div className="font-semibold flex items-center gap-2">{b.name}
                  {b.is_default && <Badge variant="outline" className="text-[10px]">{t("team.mainBranch")}</Badge>}</div>
                {b.address && <div className="text-xs text-gray-600 mt-0.5">{b.address}</div>}
                {b.work_hours && <div className="text-xs text-gray-500 mt-0.5">🕐 {b.work_hours}</div>}
              </div>
              <div className="flex gap-1 shrink-0">
                <button data-testid={`edit-branch-${b.id}`} onClick={() => setBranchDialog({ ...b, schedule: b.schedule || defaultBranchSchedule() })} className="p-1.5 hover:bg-gray-100 rounded"><Pencil className="w-4 h-4" /></button>
                {branches.length > 1 && <button data-testid={`delete-branch-${b.id}`} onClick={() => deleteBranch(b.id)} className="p-1.5 hover:bg-red-50 text-red-600 rounded"><Trash2 className="w-4 h-4" /></button>}
              </div>
            </div>
          ))}
        </div>
      </section>

      {/* ── Staff ── */}
      <section className="bg-white border-2 border-black rounded-lg p-4 sm:p-6">
        <div className="flex items-center justify-between flex-wrap gap-3 mb-4">
          <h3 className="text-lg font-bold flex items-center gap-2"><Users className="w-5 h-5" /> {t("team.staffTitle")}</h3>
          <Button size="sm" data-testid="add-staff-btn" onClick={() => setStaffDialog({ ...emptyStaff, branch_ids: staffBranchTab !== "all" ? [staffBranchTab] : [], color: STAFF_COLORS[staff.length % STAFF_COLORS.length] })}
            className="bg-black text-white hover:bg-gray-800 font-semibold"><Plus className="w-4 h-4 mr-1" />{t("team.addStaff")}</Button>
        </div>
        <p className="text-xs text-gray-500 mb-3">{t("team.staffHint")}</p>
        {branches.length > 1 && (
          <div className="flex flex-wrap gap-1.5 mb-3" data-testid="staff-branch-tabs">
            {[{ id: "all", name: t("team.allBranches") }, ...branches].map((b) => (
              <button key={b.id} type="button" onClick={() => setStaffBranchTab(b.id)} data-testid={`staff-branch-tab-${b.id}`}
                className={`text-xs px-3 py-1.5 rounded-full border-2 font-semibold ${staffBranchTab === b.id ? "bg-black text-white border-black" : "border-gray-300 text-gray-600 hover:border-black"}`}>
                {b.name} <span className="font-normal opacity-70">· {staff.filter((st) => staffInBranch(st, b.id)).length}</span>
              </button>
            ))}
          </div>
        )}
        <div className="space-y-2" data-testid="staff-list">
          {staff.length === 0 && <p className="text-sm text-gray-400 text-center py-4">{t("team.noStaff")}</p>}
          {staff.filter((st) => staffInBranch(st, staffBranchTab)).map((s) => (
            <div key={s.id} className="border-2 border-gray-200 rounded-md p-3 flex flex-wrap items-center gap-3">
              {s.photo_url
                ? <img src={s.photo_url} alt="" className="w-9 h-9 rounded-full object-cover shrink-0 border-2" style={{ borderColor: s.color || "#0ea5e9" }} data-testid={`staff-row-photo-${s.id}`} />
                : <span className="w-3 h-3 rounded-full shrink-0" style={{ background: s.color || "#0ea5e9" }} />}
              <div className="min-w-0 flex-1">
                <div className="font-semibold flex items-center gap-2 flex-wrap">
                  {s.name}
                  {s.level && levels.find((l) => l.id === s.level) && <span className="ml-1 text-amber-500 text-xs" title={levels.find((l) => l.id === s.level).name} data-testid={`staff-level-${s.id}`}>{"★".repeat(levels.find((l) => l.id === s.level).stars)}</span>}
                  <Badge className={s.role === "admin" ? "bg-purple-600 text-white" : "bg-emerald-600 text-white"}>
                    {s.role === "admin" ? t("team.roleAdmin") : t("team.roleMaster")}
                  </Badge>
                  {s.joined ? <Badge className="bg-green-100 text-green-800 border border-green-300">✓ {t("team.joined")}</Badge>
                    : s.invited ? <Badge className="bg-amber-100 text-amber-800 border border-amber-300">✉ {t("team.invited")}</Badge> : null}
                </div>
                <div className="text-xs text-gray-500 mt-0.5">
                  {s.email || t("team.noEmail")}
                  {(s.branch_ids || []).length > 0 && <> · 📍 {s.branch_ids.map(branchName).filter(Boolean).join(", ")}</>}
                  {(s.services || []).length > 0 && <> · {s.services.join(", ")}</>}
                </div>
                {s.role !== "admin" && strictMode && !(s.services || []).length && !Object.keys(s.service_overrides || {}).length && (
                  <button type="button" onClick={() => setStaffDialog({ ...emptyStaff, ...s })} data-testid={`staff-no-services-warning-${s.id}`}
                    className="mt-1.5 inline-flex items-center gap-1.5 text-xs font-semibold text-amber-800 bg-amber-50 border border-amber-300 rounded-md px-2 py-1 hover:bg-amber-100">
                    ⚠️ {t("team.noServicesWarning")}
                  </button>
                )}
              </div>
              <div className="flex gap-1 shrink-0">
                {s.email && (
                  <Button size="sm" variant="outline" data-testid={`invite-staff-${s.id}`} onClick={() => inviteStaff(s.id)} disabled={inviteBusy}
                    title={s.joined ? t("team.resendAccess") : ""}
                    className="border-2 border-black text-xs font-semibold h-8"><Mail className="w-3.5 h-3.5 mr-1" />{s.joined ? t("team.resendAccess") : s.invited ? t("team.reinvite") : t("team.invite")}</Button>
                )}
                <button data-testid={`schedule-staff-${s.id}`} title={t("team.schedule")} onClick={() => setScheduleDialog(JSON.parse(JSON.stringify(s)))} className="p-1.5 hover:bg-gray-100 rounded"><Clock className="w-4 h-4" /></button>
                <button data-testid={`edit-staff-${s.id}`} onClick={() => setStaffDialog({ ...emptyStaff, ...s })} className="p-1.5 hover:bg-gray-100 rounded"><Pencil className="w-4 h-4" /></button>
                <button data-testid={`delete-staff-${s.id}`} onClick={() => deleteStaff(s.id)} className="p-1.5 hover:bg-red-50 text-red-600 rounded"><Trash2 className="w-4 h-4" /></button>
              </div>
            </div>
          ))}
        </div>
      </section>

      {/* ── Services ── */}
      <section className="bg-white border-2 border-black rounded-lg p-4 sm:p-6">
        <div className="flex items-center justify-between flex-wrap gap-3 mb-4">
          <h3 className="text-lg font-bold flex items-center gap-2"><Scissors className="w-5 h-5" /> {t("team.servicesTitle")}</h3>
          <div className="flex gap-2">
            <Button size="sm" variant="outline" data-testid="levels-btn" onClick={() => setLevelsOpen(true)} className="border-2 border-black font-semibold"><Star className="w-4 h-4 mr-1" />{t("team.levels")}</Button>
            <Button size="sm" data-testid="add-service-btn" onClick={() => setServiceDialog({ ...emptyService, branch_ids: svcBranchTab !== "all" ? [svcBranchTab] : [] })}
              className="bg-black text-white hover:bg-gray-800 font-semibold"><Plus className="w-4 h-4 mr-1" />{t("team.addService")}</Button>
          </div>
        </div>
        <LevelsEditor open={levelsOpen} onClose={() => setLevelsOpen(false)} levels={levels} onSaved={(l) => { setLevels(l); load(); }} t={t} />
        <p className="text-xs text-gray-500 mb-3">{t("team.servicesHint")}</p>
        {branches.length > 1 && (
          <div className="flex flex-wrap gap-1.5 mb-3" data-testid="services-branch-tabs">
            {[{ id: "all", name: t("team.allBranches") }, ...branches].map((b) => (
              <button key={b.id} type="button" onClick={() => setSvcBranchTab(b.id)} data-testid={`services-branch-tab-${b.id}`}
                className={`text-xs px-3 py-1.5 rounded-full border-2 font-semibold ${svcBranchTab === b.id ? "bg-black text-white border-black" : "border-gray-300 text-gray-600 hover:border-black"}`}>
                {b.name} <span className="font-normal opacity-70">· {services.filter((sv) => svcInBranch(sv, b.id)).length}</span>
              </button>
            ))}
          </div>
        )}
        <div className="space-y-3" data-testid="services-list">
          {services.length === 0 && <p className="text-sm text-gray-400 text-center py-4">{t("team.noServices")}</p>}
          {(() => { const groups = groupByCategory(services.filter((sv) => svcInBranch(sv, svcBranchTab)), t("team.noCategory")); return groups.map((g, gi) => {
            const ck = g.key || "_none";
            const isOpen = !!openCats[ck];
            return (
            <div key={ck} data-testid={`services-category-${g.key || "none"}`} className="border-2 border-gray-200 rounded-md">
              <div className="flex items-center">
                {g.key && svcBranchTab === "all" && (
                  <div className="flex flex-col pl-1.5 -mr-1">
                    <button type="button" onClick={() => moveCategory(groups, gi, -1)} className="text-[10px] leading-none text-gray-400 hover:text-black px-1" data-testid={`category-up-${g.key}`} title="↑">▲</button>
                    <button type="button" onClick={() => moveCategory(groups, gi, 1)} className="text-[10px] leading-none text-gray-400 hover:text-black px-1" data-testid={`category-down-${g.key}`} title="↓">▼</button>
                  </div>
                )}
                <button type="button" onClick={() => toggleCat(ck)} data-testid={`services-category-toggle-${g.key || "none"}`}
                  className="flex-1 flex items-center justify-between px-3 py-2 text-left hover:bg-gray-50 rounded-md">
                  <span className="text-xs font-bold uppercase tracking-wide text-gray-700">{g.key ? g.label : t("team.allServices")} <span className="font-normal text-gray-400">· {g.list.length}</span></span>
                  <span className="text-base leading-none text-gray-400">{isOpen ? "▴" : "▾"}</span>
                </button>
              </div>
              {isOpen && (
              <div className="grid sm:grid-cols-2 gap-2 px-3 pb-3">
              {g.list.map((sv, si) => (
            <div key={sv.id} className="border-2 border-gray-200 rounded-md px-3 py-2 flex items-center justify-between gap-2">
              {svcBranchTab === "all" && (
                <div className="flex flex-col -ml-1.5 shrink-0">
                  <button type="button" onClick={() => moveService(g.list, si, -1)} disabled={si === 0} className="text-[10px] leading-none text-gray-400 hover:text-black disabled:opacity-20 px-1" data-testid={`service-up-${sv.id}`}>▲</button>
                  <button type="button" onClick={() => moveService(g.list, si, 1)} disabled={si === g.list.length - 1} className="text-[10px] leading-none text-gray-400 hover:text-black disabled:opacity-20 px-1" data-testid={`service-down-${sv.id}`}>▼</button>
                </div>
              )}
              <div className="min-w-0 flex-1">
                <div className="font-medium text-sm truncate">{sv.name}</div>
                <div className="text-xs text-gray-500">{sv.duration_min} {t("team.min")}{sv.price != null && <> · {sv.price}</>}
                  {Object.keys(sv.level_prices || {}).length > 0 && <span className="ml-1 text-gray-400" data-testid={`service-level-prices-${sv.id}`}>· {levels.filter((l) => sv.level_prices[l.id] != null).map((l) => `${l.name} ${sv.level_prices[l.id]}`).join(" · ")}</span>}
                  {Object.keys(sv.branch_prices || {}).length > 0 && <span className="ml-1 text-gray-400" data-testid={`service-branch-prices-${sv.id}`}>· {branches.filter((b) => sv.branch_prices[b.id] != null).map((b) => `${b.name} ${sv.branch_prices[b.id]}`).join(" · ")}</span>}</div>
                {branches.length > 1 && (sv.branch_ids || []).length > 0 && (
                  <div className="flex flex-wrap gap-1 mt-1" data-testid={`service-branches-${sv.id}`}>
                    {sv.branch_ids.map((bid) => branches.find((b) => b.id === bid)).filter(Boolean).map((b) => (
                      <span key={b.id} className="text-[10px] px-1.5 py-0.5 rounded bg-gray-100 border border-gray-200 text-gray-600">📍 {b.name}</span>
                    ))}
                  </div>
                )}
              </div>
              <div className="flex gap-1 shrink-0">
                <button data-testid={`duplicate-service-${sv.id}`} title={t("team.duplicate")} onClick={() => setServiceDialog({ ...sv, id: undefined, name: `${sv.name} (${t("team.copySuffix")})`, price: sv.price ?? "", category: sv.category || "", branch_ids: [...(sv.branch_ids || [])], branch_prices: { ...(sv.branch_prices || {}) }, level_prices: { ...(sv.level_prices || {}) } })} className="p-1.5 hover:bg-gray-100 rounded"><Copy className="w-4 h-4" /></button>
                <button data-testid={`edit-service-${sv.id}`} onClick={() => setServiceDialog({ ...sv, price: sv.price ?? "", category: sv.category || "", branch_ids: sv.branch_ids || [], branch_prices: sv.branch_prices || {} })} className="p-1.5 hover:bg-gray-100 rounded"><Pencil className="w-4 h-4" /></button>
                <button data-testid={`delete-service-${sv.id}`} onClick={() => deleteService(sv.id)} className="p-1.5 hover:bg-red-50 text-red-600 rounded"><Trash2 className="w-4 h-4" /></button>
              </div>
            </div>
              ))}
              </div>
              )}
            </div>
            );
          }); })()}
        </div>
      </section>

      {/* ── Branch dialog ── */}
      <Dialog open={!!branchDialog} onOpenChange={(o) => !o && setBranchDialog(null)}>
        <DialogContent className="text-black border-2 border-black">
          <DialogHeader><DialogTitle>{branchDialog?.id ? t("team.editBranch") : t("team.addBranch")}</DialogTitle></DialogHeader>
          {branchDialog && (
            <div className="space-y-3">
              <div><Label>{t("team.branchName")}</Label>
                <Input data-testid="branch-name-input" value={branchDialog.name} onChange={(e) => setBranchDialog({ ...branchDialog, name: e.target.value })} className="border-2 border-black" placeholder="Ницца — Центр" /></div>
              <div><Label>{t("team.branchAddress")}</Label>
                <Input data-testid="branch-address-input" value={branchDialog.address || ""} onChange={(e) => setBranchDialog({ ...branchDialog, address: e.target.value })} className="border-2 border-black" /></div>
              <div><Label>{t("team.branchPhone")}</Label>
                <Input value={branchDialog.phone || ""} onChange={(e) => setBranchDialog({ ...branchDialog, phone: e.target.value })} className="border-2 border-black" /></div>
              <div>
                <Label>{t("team.branchHours")}</Label>
                <div className="space-y-1.5 mt-1 border-2 border-gray-200 rounded-md p-3">
                  {DAYS.map((d) => {
                    const sched = branchDialog.schedule || defaultBranchSchedule();
                    const day = sched[d] || { enabled: false, start: "10:00", end: "19:00" };
                    const upd = (patch) => setBranchDialog({
                      ...branchDialog,
                      schedule: { ...sched, [d]: { ...day, ...patch } },
                    });
                    return (
                      <div key={d} className="flex items-center gap-2">
                        <button type="button" data-testid={`branch-sched-${d}-toggle`} onClick={() => upd({ enabled: !day.enabled })}
                          className={`w-11 text-xs font-bold py-1.5 rounded border-2 ${day.enabled ? "bg-black text-white border-black" : "border-gray-300 text-gray-400"}`}>
                          {DAY_RU[d]}
                        </button>
                        {day.enabled ? (
                          <>
                            <Input type="time" value={day.start} onChange={(e) => upd({ start: e.target.value })} className="border-2 border-black w-26 h-8 text-xs" />
                            <span className="text-gray-400">—</span>
                            <Input type="time" value={day.end} onChange={(e) => upd({ end: e.target.value })} className="border-2 border-black w-26 h-8 text-xs" />
                          </>
                        ) : <span className="text-xs text-gray-400">{t("team.dayOff")}</span>}
                      </div>
                    );
                  })}
                </div>
                <p className="text-[11px] text-gray-400 mt-1">{t("team.branchHoursHint")}</p>
              </div>
            </div>
          )}
          <DialogFooter>
            <Button data-testid="save-branch-btn" onClick={saveBranch} disabled={busy || !branchDialog?.name?.trim()} className="bg-black text-white font-semibold">{t("team.save")}</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* ── Staff dialog ── */}
      <Dialog open={!!staffDialog} onOpenChange={(o) => !o && setStaffDialog(null)}>
        <DialogContent className="text-black border-2 border-black max-h-[85vh] overflow-y-auto">
          <DialogHeader><DialogTitle>{staffDialog?.id ? t("team.editStaff") : t("team.addStaff")}</DialogTitle></DialogHeader>
          {staffDialog && (
            <div className="space-y-3">
              <StaffPhotoUpload value={staffDialog.photo_url || ""} name={staffDialog.name} color={staffDialog.color} t={t}
                onChange={(url) => setStaffDialog({ ...staffDialog, photo_url: url })} />
              <div><Label>{t("team.staffName")}</Label>
                <Input data-testid="staff-name-input" value={staffDialog.name} onChange={(e) => setStaffDialog({ ...staffDialog, name: e.target.value })} className="border-2 border-black" /></div>
              <div><Label>Email ({t("team.forInvite")})</Label>
                <div className="flex gap-2">
                  <Input data-testid="staff-email-input" type="email" value={staffDialog.email || ""} onChange={(e) => setStaffDialog({ ...staffDialog, email: e.target.value })} className="border-2 border-black flex-1" />
                  {staffDialog.id && staffDialog.email && (
                    <Button type="button" size="sm" variant="outline" data-testid="staff-resend-invite" disabled={inviteBusy}
                      title={staffDialog.joined ? t("team.resendAccess") : t("team.reinvite")}
                      onClick={() => inviteStaff(staffDialog.id)}
                      className="border-2 border-black text-xs font-semibold h-10 whitespace-nowrap">
                      <Mail className="w-3.5 h-3.5 mr-1" />{staffDialog.joined ? t("team.resendAccess") : staffDialog.invited ? t("team.reinvite") : t("team.invite")}
                    </Button>
                  )}
                </div>
                {staffDialog.id && staffDialog.email && <p className="text-[11px] text-gray-500 mt-1">{t("team.resendHint")}</p>}</div>
              <div><Label>{t("team.role")}</Label>
                <Select value={staffDialog.role} onValueChange={(v) => setStaffDialog({ ...staffDialog, role: v })}>
                  <SelectTrigger data-testid="staff-role-select" className="border-2 border-black"><SelectValue /></SelectTrigger>
                  <SelectContent>
                    <SelectItem value="master">{t("team.roleMaster")} — {t("team.roleMasterDesc")}</SelectItem>
                    <SelectItem value="admin">{t("team.roleAdmin")} — {t("team.roleAdminDesc")}</SelectItem>
                  </SelectContent>
                </Select></div>
              {staffDialog.role === "master" && levels.length > 0 && (
                <div><Label className="flex items-center gap-1"><Star className="w-3.5 h-3.5" /> {t("team.level")}</Label>
                  <Select value={staffDialog.level || "none"} onValueChange={(v) => setStaffDialog({ ...staffDialog, level: v === "none" ? null : v })}>
                    <SelectTrigger data-testid="staff-level-select" className="border-2 border-black"><SelectValue /></SelectTrigger>
                    <SelectContent>
                      <SelectItem value="none">{t("team.levelNone")}</SelectItem>
                      {levels.map((l) => <SelectItem key={l.id} value={l.id}>{"★".repeat(l.stars)} {l.name}</SelectItem>)}
                    </SelectContent>
                  </Select></div>
              )}
              {staffDialog.role === "admin" && myRole === "owner" && (
                <div data-testid="admin-perms-block">
                  <Label>{t("team.permsTitle")}</Label>
                  <div className="flex flex-wrap gap-2 mt-1">
                    {[["team", t("tabs.team")]].map(([k, label]) => {
                      const perms = { bookings: true, clients: true, team: true, ...(staffDialog.permissions || {}) };
                      const on = perms[k] !== false;
                      return (
                        <button key={k} type="button" data-testid={`perm-${k}-toggle`}
                          onClick={() => setStaffDialog({ ...staffDialog, permissions: { ...perms, [k]: !on } })}
                          className={`text-xs px-3 py-1.5 rounded-full border-2 font-semibold ${on ? "bg-black text-white border-black" : "border-gray-300 text-gray-400 line-through"}`}>
                          {label}
                        </button>
                      );
                    })}
                  </div>
                  <p className="text-[11px] text-gray-400 mt-1">{t("team.permsHint")}</p>
                </div>
              )}
              {branches.length > 1 && (
                <div><Label>{t("team.branches")}</Label>
                  <div className="flex flex-wrap gap-2 mt-1">
                    {branches.map((b) => {
                      const on = (staffDialog.branch_ids || []).includes(b.id);
                      return (
                        <button key={b.id} type="button" onClick={() => setStaffDialog({
                          ...staffDialog,
                          branch_ids: on ? staffDialog.branch_ids.filter((x) => x !== b.id) : [...(staffDialog.branch_ids || []), b.id],
                        })} className={`text-xs px-3 py-1.5 rounded-full border-2 font-semibold ${on ? "bg-black text-white border-black" : "border-gray-300 text-gray-600"}`}>
                          {b.name}
                        </button>
                      );
                    })}
                  </div>
                </div>
              )}
              {staffDialog.level && (
                <label className="flex items-start gap-2 cursor-pointer border-2 border-gray-200 rounded-md p-2.5" data-testid="staff-accepts-lower-block">
                  <input type="checkbox" className="mt-0.5" checked={!!staffDialog.accepts_lower_levels} data-testid="staff-accepts-lower-toggle"
                    onChange={(e) => setStaffDialog({ ...staffDialog, accepts_lower_levels: e.target.checked })} />
                  <span className="text-xs"><span className="font-semibold">{t("team.acceptsLower")}</span><br /><span className="text-gray-500">{t("team.acceptsLowerHint")}</span></span>
                </label>
              )}
              <div><Label>{t("team.staffServices")}</Label>
                <p className="text-[11px] text-gray-500">{t("team.staffServicesHint")}</p>
                <div className="space-y-2 mt-1">
                  {groupByCategory(services.filter((sv) => svcForStaff(sv, staffDialog)), t("team.noCategory")).map((g) => {
                    const sel = staffDialog.services || [];
                    const names = g.list.map((sv) => sv.name);
                    const allOn = names.length > 0 && names.every((n) => sel.includes(n));
                    const toggleCat = () => setStaffDialog({
                      ...staffDialog,
                      services: allOn ? sel.filter((x) => !names.includes(x)) : [...new Set([...sel, ...names])],
                    });
                    return (
                      <div key={g.key || "_none"} data-testid={`staff-services-category-${g.key || "none"}`}>
                        {(g.key || services.some((s) => (s.category || "").trim())) && (
                          <button type="button" onClick={toggleCat} data-testid={`staff-category-toggle-${g.key || "none"}`}
                            className={`flex items-center gap-1.5 text-xs font-bold uppercase tracking-wide mb-1 ${allOn ? "text-black" : "text-gray-500 hover:text-black"}`}>
                            <span className={`w-3.5 h-3.5 rounded border-2 flex items-center justify-center ${allOn ? "bg-black border-black text-white" : "border-gray-400"}`}>{allOn ? "✓" : ""}</span>
                            {g.label} <span className="font-normal normal-case text-gray-400">· {t("team.wholeCategory")}</span>
                          </button>
                        )}
                        <div className="flex flex-wrap gap-2">
                          {g.list.map((sv) => {
                            const on = sel.includes(sv.name);
                            const ov = (staffDialog.service_overrides || {})[sv.name] || {};
                            const setOv = (patch) => setStaffDialog({ ...staffDialog, service_overrides: { ...(staffDialog.service_overrides || {}), [sv.name]: { ...ov, ...patch } } });
                            return (
                              <div key={sv.id} className={`flex items-center gap-1 rounded-full border-2 ${on ? "border-black bg-black text-white" : "border-gray-300 text-gray-600"}`}>
                                <button type="button" data-testid={`staff-service-toggle-${sv.id}`} onClick={() => setStaffDialog({
                                  ...staffDialog,
                                  services: on ? sel.filter((x) => x !== sv.name) : [...sel, sv.name],
                                })} className="text-xs pl-3 pr-1 py-1.5 font-semibold">
                                  {sv.name}
                                </button>
                                {on && (
                                  <span className="flex items-center gap-0.5 pr-1.5 text-[11px]" title={t("team.ownDurationPrice")}>
                                    <input type="number" min="5" step="5" value={ov.duration_min ?? ""} placeholder={sv.duration_min}
                                      onChange={(e) => setOv({ duration_min: e.target.value === "" ? null : parseInt(e.target.value) })}
                                      className="w-11 h-6 rounded bg-white text-black text-[11px] px-1 text-center" data-testid={`staff-service-duration-${sv.id}`} />
                                    <span className="opacity-70">{t("team.min")}</span>
                                    <input type="number" min="0" step="0.5" value={ov.price ?? ""} placeholder={sv.price ?? "—"}
                                      onChange={(e) => setOv({ price: e.target.value === "" ? null : parseFloat(e.target.value) })}
                                      className="w-12 h-6 rounded bg-white text-black text-[11px] px-1 text-center ml-1" data-testid={`staff-service-price-${sv.id}`} />
                                    <span className="opacity-70">€</span>
                                  </span>
                                )}
                              </div>
                            );
                          })}
                        </div>
                      </div>
                    );
                  })}
                  {services.length === 0 && <span className="text-xs text-gray-400">{t("team.addServicesFirst")}</span>}
                </div>
              </div>
              <div><Label>{t("team.color")}</Label>
                <div className="flex gap-2 mt-1">
                  {STAFF_COLORS.map((c) => (
                    <button key={c} type="button" onClick={() => setStaffDialog({ ...staffDialog, color: c })}
                      className={`w-7 h-7 rounded-full border-2 ${staffDialog.color === c ? "border-black scale-110" : "border-transparent"}`} style={{ background: c }} />
                  ))}
                </div>
              </div>
              {staffDialog.role === "master" && (
                <div className="pt-3 border-t border-gray-200">
                  <Label className="flex items-center gap-1.5"><Clock className="w-3.5 h-3.5" />{t("team.planityTitle")}</Label>
                  <p className="text-[11px] text-gray-500 mb-1.5">{t("team.planityHint")}</p>
                  <div className="flex gap-2">
                    <Input data-testid="staff-planity-input" value={staffDialog.planity_ical_url || ""}
                      onChange={(e) => { setStaffDialog({ ...staffDialog, planity_ical_url: e.target.value }); setPlanityCheck(null); }}
                      placeholder="https://...planityapp.com/getCalendarIcalEvents?..." className="border-2 border-black text-xs" />
                    <Button type="button" data-testid="staff-planity-check-btn" onClick={checkPlanity}
                      disabled={!(staffDialog.planity_ical_url || "").trim() || planityCheck?.loading}
                      className="bg-black text-white font-semibold shrink-0">
                      {planityCheck?.loading ? "…" : t("team.planityCheck")}
                    </Button>
                  </div>
                  {planityCheck?.result && (
                    <div data-testid="staff-planity-result" className="mt-2 text-xs bg-green-50 border border-green-200 rounded-lg p-2 text-green-800">
                      ✓ {planityCheck.result.calendar_name || "Planity"} — {t("team.planityFound")}: <b>{planityCheck.result.busy_count}</b>
                    </div>
                  )}
                  {planityCheck?.error && (
                    <div data-testid="staff-planity-error" className="mt-2 text-xs bg-red-50 border border-red-200 rounded-lg p-2 text-red-700">{planityCheck.error}</div>
                  )}
                </div>
              )}
            </div>
          )}
          <DialogFooter>
            <Button data-testid="save-staff-btn" onClick={saveStaff} disabled={busy || !staffDialog?.name?.trim()} className="bg-black text-white font-semibold">{t("team.save")}</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* ── Schedule dialog ── */}
      <Dialog open={!!scheduleDialog} onOpenChange={(o) => !o && setScheduleDialog(null)}>
        <DialogContent className="text-black border-2 border-black max-h-[85vh] overflow-y-auto">
          <DialogHeader><DialogTitle>{t("team.scheduleFor")} {scheduleDialog?.name}</DialogTitle></DialogHeader>
          {scheduleDialog && (
            <div className="space-y-2">
              {DAYS.map((d) => {
                const day = (scheduleDialog.schedule || {})[d] || { enabled: false, start: "10:00", end: "19:00" };
                const upd = (patch) => setScheduleDialog({
                  ...scheduleDialog,
                  schedule: { ...(scheduleDialog.schedule || {}), [d]: { ...day, ...patch } },
                });
                return (
                  <div key={d} className="flex items-center gap-3">
                    <button type="button" data-testid={`sched-${d}-toggle`} onClick={() => upd({ enabled: !day.enabled })}
                      className={`w-12 text-sm font-bold py-1.5 rounded border-2 ${day.enabled ? "bg-black text-white border-black" : "border-gray-300 text-gray-400"}`}>
                      {DAY_RU[d]}
                    </button>
                    {day.enabled ? (
                      <>
                        <Input type="time" value={day.start} onChange={(e) => upd({ start: e.target.value })} className="border-2 border-black w-28 h-9" />
                        <span className="text-gray-400">—</span>
                        <Input type="time" value={day.end} onChange={(e) => upd({ end: e.target.value })} className="border-2 border-black w-28 h-9" />
                      </>
                    ) : <span className="text-sm text-gray-400">{t("team.dayOff")}</span>}
                  </div>
                );
              })}
              {(() => {
                const rep = scheduleDialog.schedule_repeat || {};
                const every = parseInt(rep.every_weeks) || 1;
                const setRep = (patch) => setScheduleDialog({ ...scheduleDialog, schedule_repeat: { ...rep, every_weeks: every, ...patch } });
                return (
                  <div className="pt-3 mt-1 border-t border-gray-200" data-testid="schedule-repeat">
                    <Label className="font-bold">🔁 {t("team.repeat")}</Label>
                    <p className="text-[11px] text-gray-500 mb-2">{t("team.repeatHint")}</p>
                    <div className="flex flex-wrap items-center gap-2">
                      {[1, 2, 3, 4].map((n) => (
                        <button key={n} type="button" onClick={() => setRep({ every_weeks: n, anchor: rep.anchor || new Date().toISOString().slice(0, 10) })} data-testid={`repeat-every-${n}`}
                          className={`text-xs px-3 py-1.5 rounded-full border-2 font-semibold ${every === n ? "bg-black text-white border-black" : "border-gray-300 text-gray-600"}`}>
                          {n === 1 ? t("team.repeatWeekly") : t("team.repeatEveryN", { n })}
                        </button>
                      ))}
                    </div>
                    {every > 1 && (
                      <div className="grid grid-cols-2 gap-2 mt-2">
                        <div><Label className="text-xs">{t("team.repeatAnchor")}</Label>
                          <Input type="date" value={rep.anchor || ""} onChange={(e) => setRep({ anchor: e.target.value })} className="border-2 border-black h-9" data-testid="repeat-anchor" /></div>
                        <div><Label className="text-xs">{t("team.repeatUntil")}</Label>
                          <Input type="date" value={rep.until || ""} onChange={(e) => setRep({ until: e.target.value || null })} className="border-2 border-black h-9" data-testid="repeat-until" /></div>
                      </div>
                    )}
                  </div>
                );
              })()}
              {scheduleDialog.id && (
                <StaffExceptions staff={scheduleDialog} t={t}
                  onChanged={(ov) => { setScheduleDialog({ ...scheduleDialog, date_overrides: ov }); load(); }} />
              )}
            </div>
          )}
          <DialogFooter>
            <Button data-testid="save-schedule-btn" onClick={saveSchedule} disabled={busy} className="bg-black text-white font-semibold">{t("team.save")}</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* ── Service dialog ── */}
      <Dialog open={!!serviceDialog} onOpenChange={(o) => !o && setServiceDialog(null)}>
        <DialogContent className="text-black border-2 border-black">
          <DialogHeader><DialogTitle>{serviceDialog?.id ? t("team.editService") : t("team.addService")}</DialogTitle></DialogHeader>
          {serviceDialog && (
            <div className="space-y-3">
              <div><Label>{t("team.serviceName")}</Label>
                <Input data-testid="service-name-input" value={serviceDialog.name} onChange={(e) => setServiceDialog({ ...serviceDialog, name: e.target.value })} className="border-2 border-black" placeholder="Маникюр" /></div>
              <div><Label>{t("team.category")}</Label>
                <Input data-testid="service-category-input" list="service-categories" value={serviceDialog.category || ""} onChange={(e) => setServiceDialog({ ...serviceDialog, category: e.target.value })} className="border-2 border-black" placeholder={t("team.categoryPlaceholder")} />
                <datalist id="service-categories">
                  {[...new Set(services.map((s) => (s.category || "").trim()).filter(Boolean))].map((c) => <option key={c} value={c} />)}
                </datalist>
              </div>
              <div className="grid grid-cols-2 gap-3">
                <div><Label>{t("team.servicePrice")}</Label>
                  <Input data-testid="service-price-input" type="number" value={serviceDialog.price} onChange={(e) => setServiceDialog({ ...serviceDialog, price: e.target.value })} className="border-2 border-black" /></div>
                <div><Label>{t("team.serviceDuration")}</Label>
                  <Input data-testid="service-duration-input" type="number" step="15" min="15" value={serviceDialog.duration_min} onChange={(e) => setServiceDialog({ ...serviceDialog, duration_min: e.target.value })} className="border-2 border-black" /></div>
              </div>
              {branches.length > 1 && (
                <div data-testid="service-branches-block">
                  <Label>{t("team.serviceBranches")}</Label>
                  <p className="text-[11px] text-gray-500 mb-1.5">{t("team.serviceBranchesHint")}</p>
                  <div className="flex flex-wrap gap-2">
                    {branches.map((b) => {
                      const on = (serviceDialog.branch_ids || []).includes(b.id);
                      return (
                        <button key={b.id} type="button" data-testid={`service-branch-toggle-${b.id}`} onClick={() => setServiceDialog({
                          ...serviceDialog,
                          branch_ids: on ? serviceDialog.branch_ids.filter((x) => x !== b.id) : [...(serviceDialog.branch_ids || []), b.id],
                        })} className={`text-xs px-3 py-1.5 rounded-full border-2 font-semibold ${on ? "bg-black text-white border-black" : "border-gray-300 text-gray-600"}`}>📍 {b.name}</button>
                      );
                    })}
                  </div>
                  <div className="grid grid-cols-1 sm:grid-cols-2 gap-2 mt-2" data-testid="service-branch-prices">
                    {branches.filter((b) => !(serviceDialog.branch_ids || []).length || serviceDialog.branch_ids.includes(b.id)).map((b) => (
                      <div key={b.id} className="flex items-center gap-2">
                        <span className="text-xs w-36 shrink-0 truncate" title={b.name}>{t("team.priceIn")} {b.name}</span>
                        <Input data-testid={`service-branch-price-${b.id}`} type="number" placeholder={serviceDialog.price || "—"} value={serviceDialog.branch_prices?.[b.id] ?? ""}
                          onChange={(e) => setServiceDialog({ ...serviceDialog, branch_prices: { ...(serviceDialog.branch_prices || {}), [b.id]: e.target.value } })} className="border-2 border-gray-300 h-8" />
                      </div>
                    ))}
                  </div>
                </div>
              )}
              {levels.length > 0 && (
                <div data-testid="service-level-prices">
                  <Label className="flex items-center gap-1"><Star className="w-3.5 h-3.5" /> {t("team.priceByLevel")}</Label>
                  <p className="text-[11px] text-gray-500 mb-1.5">{t("team.priceByLevelHint")}</p>
                  <div className="grid grid-cols-1 sm:grid-cols-2 gap-2">
                    {levels.map((l) => (
                      <div key={l.id} className="flex items-center gap-2">
                        <span className="text-xs w-36 shrink-0 truncate flex items-center gap-1" title={l.name}><Stars n={l.stars} size={10} />{l.name}</span>
                        <Input data-testid={`service-level-price-${l.id}`} type="number" placeholder={serviceDialog.price || "—"} value={serviceDialog.level_prices?.[l.id] ?? ""}
                          onChange={(e) => setServiceDialog({ ...serviceDialog, level_prices: { ...(serviceDialog.level_prices || {}), [l.id]: e.target.value } })} className="border-2 border-gray-300 h-8" />
                      </div>
                    ))}
                  </div>
                </div>
              )}
            </div>
          )}
          <DialogFooter>
            <Button data-testid="save-service-btn" onClick={saveService} disabled={busy || !serviceDialog?.name?.trim()} className="bg-black text-white font-semibold">{t("team.save")}</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
